import type { ReactNode } from 'react';
import styles from './Panel.module.css';

/**
 * A titled block: heading, optional caption and right-hand controls, content.
 *
 * The Availability page's DataTable draws its own frame, sized to the Figma
 * comp. The pages added since carry sections a fixed-width table cannot hold —
 * a chart, a worklist, a competitor board — and they all need the same shell.
 * This is that shell, and it deliberately matches DataTable's border, radius
 * and header weight so a page mixing the two does not look like two products.
 */
export function Panel({
  title,
  caption,
  actions,
  footnote,
  children,
  dense,
}: {
  title: string;
  /** One line under the title. Use it to say what the numbers ARE. */
  caption?: string | undefined;
  actions?: ReactNode;
  /**
   * Small print at the bottom. This is where a panel states its own limits —
   * which basis it read, what it excluded, what it cannot answer.
   */
  footnote?: string | undefined;
  children: ReactNode;
  dense?: boolean;
}) {
  return (
    <section className={`${styles.panel} ${dense ? styles.dense : ''}`}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h2 className={styles.title}>{title}</h2>
          {caption ? <p className={styles.caption}>{caption}</p> : null}
        </div>
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </header>
      <div className={styles.body}>{children}</div>
      {footnote ? <p className={styles.footnote}>{footnote}</p> : null}
    </section>
  );
}

/** A row of mutually exclusive choices, styled as the page's own tab strip. */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className={styles.segmented} role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`${styles.segment} ${o.value === value ? styles.segmentOn : ''}`}
          aria-pressed={o.value === value}
          onClick={() => { onChange(o.value); }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * What a panel says when it has nothing to show.
 *
 * Always a sentence, never an empty box. An empty section that cannot explain
 * itself reads as "nothing to do here", which on a worklist is the most
 * expensive thing a page can say wrongly.
 */
export function EmptyState({ children }: { children: ReactNode }) {
  return <p className={styles.empty}>{children}</p>;
}
